import BattleStateManager from '../../game/BattleStateManager.js';
import { showMarqueeMessage } from '../../Utils.js';

const DEFAULT_SHOP_ITEMS = [
  { id: 'potion_heal', name: 'Healing Potion', cost: 300, type: 'healing', effect: { heal: 100 }, iconKey: 'equip_potion_red_4', stackable: true, quantity: 1 },
  { id: 'potion_strength', name: 'Strength Potion', cost: 300, type: 'strength', effect: { attackBoost: 0.3, durationSec: 15 }, iconKey: 'equip_potion_purple_4', stackable: true, quantity: 1 },
  { id: 'potion_mana', name: 'Mana Potion', cost: 300, type: 'mana', effect: { mana: 100 }, iconKey: 'equip_potion_blue_4', stackable: true, quantity: 1 },
  { id: 'potion_poison', name: 'Poison Vial', cost: 300, type: 'poison', effect: { dot: 7, ticks: 8 }, iconKey: 'equip_potion_green_4', stackable: true, quantity: 1 }
];

export default class JoinShopManager {
  constructor(scene) {
    this.scene = scene;
    this.shopContainer = null;
    this.shopSlots = [];
    this.shopItemLookup = new Map();
    this.pendingShopItems = [];
    this.gridConfig = null;
    this.sectionWidth = null;
    this.sectionHeight = null;

    this.tokenMeta = {
      symbol: 'soulwars',
      priceUsd: 0.000046,
      decimals: 9,
    };

    this.useFallbackItems();
  }

  getItemsData() {
    return this.itemsData;
  }

  setGridConfig(gridConfig) {
    this.gridConfig = gridConfig;
  }

  attachShopContainer(container) {
    this.shopContainer = container;
  }

  useFallbackItems() {
    this.shopItemLookup.clear();
    this.itemsData = DEFAULT_SHOP_ITEMS.map((item, index) => ({
      id: `fallback-${item.id}`,
      itemId: item.id,
      themeItemId: null,
      name: item.name,
      type: item.type,
      tier: item.tier,
      effect: item.effect,
      iconKey: item.iconKey,
      stackable: item.stackable,
      quantity: Number.isFinite(item.quantity) ? item.quantity : 1,
      badge: null,
      priceUsd: item.cost,
      tokenAmount: item.cost,
      tokenSymbol: 'SOULWARS',
      tokenUsdValue: this.tokenMeta.priceUsd,
      displayOrder: index,
    }));
    this.pendingShopItems = [...this.itemsData];
    this.itemsData.forEach(item => this.registerShopItem(item));
    this.applyPendingShopItems();
  }

  async refreshManifest({ themeId, shopType = 'join', tokenSymbol = 'soulwars', limit = 6 } = {}) {
    if (!themeId) {
      console.warn('[JoinShopManager] Missing themeId for manifest request');
      return;
    }

    const apiKey = process.env.REACT_APP_SOULWARS_API_KEY || 'soulwarz';
    const params = new URLSearchParams({
      themeId,
      shopType,
      tokenSymbol,
      limit: String(limit),
    });

    try {
      const response = await fetch(`/api/shop/manifest?${params.toString()}`, {
        headers: {
          'X-API-Key': apiKey,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Manifest request failed (${response.status})`);
      }

      const payload = await response.json();
      const manifest = payload?.data || payload;

      if (!manifest?.items || manifest.items.length === 0) {
        console.warn('[JoinShopManager] Manifest returned no items; reverting to fallback set');
        this.useFallbackItems();
        return;
      }

      this.tokenMeta = {
        symbol: manifest.token?.symbol || tokenSymbol,
        priceUsd: manifest.token?.priceUsd || this.tokenMeta.priceUsd || 0.000046,
        decimals: manifest.token?.decimals ?? this.tokenMeta.decimals ?? 9,
      };

      const items = manifest.items.map((item, index) => ({
        id: item.shopItemId,
        itemId: item.itemId,
        themeItemId: item.themeItemId,
        name: item.displayName || item.name,
        type: item.type,
        tier: item.tier,
        effect: item.effect,
        iconKey: item.iconKey,
        stackable: item.stackable,
        quantity: Number.isFinite(item.quantity) ? item.quantity : 1,
        badge: item.badge || null,
        priceUsd: item.priceUsd,
        tokenAmount: item.tokenPrice?.tokenAmount ?? 0,
        tokenSymbol: (item.tokenPrice?.tokenSymbol || this.tokenMeta.symbol || 'soulwars').toUpperCase(),
        tokenUsdValue: item.tokenPrice?.usdPerToken ?? this.tokenMeta.priceUsd,
        displayOrder: item.displayOrder ?? index,
      }));

      this.setShopItems(items);
    } catch (error) {
      console.error('[JoinShopManager] Failed to load shop manifest:', error);
      if (!this.itemsData?.length) {
        this.useFallbackItems();
      }
    }
  }

  updateShopItemsFromSocket(shopData) {
    try {
      const { items, tokenSymbol } = shopData;
      
      if (!items || !Array.isArray(items)) {
        console.warn('[JoinShopManager] Invalid shop items data from socket:', shopData);
        return;
      }
      
      // Update token meta if provided
      if (tokenSymbol) {
        const firstItem = items[0];
        this.tokenMeta = {
          symbol: tokenSymbol,
          priceUsd: firstItem?.tokenPrice?.usdPerToken || this.tokenMeta?.priceUsd || 0.000046,
          decimals: this.tokenMeta?.decimals ?? 9,
        };
      }
      
      // Transform server shop items format to client format
      const clientItems = items.map((item, index) => ({
        id: item.shopItemId,
        itemId: item.itemId,
        themeItemId: item.themeItemId,
        name: item.displayName || item.name,
        type: item.type,
        tier: item.tier,
        effect: item.effect,
        iconKey: item.iconKey,
        stackable: item.stackable,
        quantity: Number.isFinite(item.quantity) ? item.quantity : 1,
        badge: item.badge || null,
        priceUsd: item.priceUsd,
        tokenAmount: item.tokenPrice?.tokenAmount ?? 0,
        tokenSymbol: (item.tokenPrice?.tokenSymbol || tokenSymbol || this.tokenMeta?.symbol || 'SOULWARS').toUpperCase(),
        tokenUsdValue: item.tokenPrice?.usdPerToken ?? this.tokenMeta?.priceUsd ?? 0,
        displayOrder: item.displayOrder ?? index,
      }));
      
      // Update shop items
      this.setShopItems(clientItems);
      console.log(`[JoinShopManager] Updated shop with ${clientItems.length} items from socket`);
    } catch (error) {
      console.error('[JoinShopManager] Error updating shop items from socket:', error);
      // Fallback to default items if update fails
      if (!this.itemsData?.length) {
        this.useFallbackItems();
      }
    }
  }

  buildShopSection(sectionWidth, sectionHeight) {
    if (!this.scene || !this.shopContainer) {
      console.warn('[JoinShopManager] Missing scene or shop container');
      return;
    }

    // Store dimensions for rebuilding later
    this.sectionWidth = sectionWidth;
    this.sectionHeight = sectionHeight;

    try { this.shopContainer.removeAll(true); } catch (_) {}

    const { add } = this.scene;
    const innerPadding = 12;
    const titlePadding = 16;

    // Background should fill the entire container height and extend down to controls section
    // The shop container has a gap (shopGap = 12px) plus padding (24px) = 36px total gap
    // Extend background downward to fill this gap
    const extendedHeight = sectionHeight + 36;
    const shopBg = add.rectangle(0, 0, sectionWidth, extendedHeight, 0x1F2937);
    shopBg.setStrokeStyle(2, 0x374151);
    shopBg.setAlpha(0.92);
    this.shopContainer.add(shopBg);

    const title = add.text(-sectionWidth / 2 + innerPadding, -sectionHeight / 2 + titlePadding, 'Shop', {
      fontSize: '28px',
      color: '#F3F4F6',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold'
    }).setOrigin(0, 0.5);
    this.shopContainer.add(title);

    const subtitle = add.text(sectionWidth / 2 - innerPadding, -sectionHeight / 2 + titlePadding, 'Type "buy #" or "balance"', {
      fontSize: '18px',
      color: '#9CA3AF',
      fontFamily: 'Arial, sans-serif'
    }).setOrigin(1, 0.5);
    this.shopContainer.add(subtitle);

    // Vertical stack layout - each item takes full width
    const slotGap = 10;
    const headerHeight = titlePadding * 2 + 20;
    const usableHeight = sectionHeight - headerHeight - innerPadding * 2;
    
    // Get actual number of items
    const items = Array.isArray(this.pendingShopItems) && this.pendingShopItems.length > 0 
      ? this.pendingShopItems 
      : (Array.isArray(this.itemsData) ? this.itemsData : []);
    const numItems = items.length;
    
    // Only create slots for items that exist
    if (numItems === 0) {
      // No items yet, don't create any slots
      this.shopSlots = [];
      return;
    }
    
    // Calculate slot height based on actual number of items
    // Ensure minimum height for readability, but allow items to use available space
    const minSlotHeight = 110; // Slightly increased for better spacing
    const calculatedSlotHeight = usableHeight / numItems;
    const slotHeight = Math.max(minSlotHeight, calculatedSlotHeight - (slotGap * (numItems - 1) / numItems));
    const slotWidth = sectionWidth - innerPadding * 2;

    const gridTop = -sectionHeight / 2 + headerHeight;
    this.shopSlots = [];

    // Create slots in a vertical stack - only for items that exist
    for (let i = 0; i < numItems; i++) {
      const slotNumber = i + 1;
      const y = gridTop + slotHeight / 2 + i * (slotHeight + slotGap);

      const card = add.container(0, y);
      this.shopContainer.add(card);

      const cardBg = add.rectangle(0, 0, slotWidth, slotHeight, 0x111827);
      cardBg.setStrokeStyle(1, 0x374151);
      // Add subtle inner rectangle for depth
      const cardBgInner = add.rectangle(0, 0, slotWidth - 2, slotHeight - 2, 0x0F172A);
      cardBgInner.setAlpha(0.3);
      card.add(cardBgInner);
      card.add(cardBg);

      // Buy command badge in upper right corner - solid background for maximum readability
      const badgeHeight = 32;
      const badgeWidth = 75;
      // Position at absolute corner with 2 px of padding
      const badgeX = slotWidth / 2 - badgeWidth / 2 - 2;
      const badgeY = -slotHeight / 2 + badgeHeight / 2 + 2;
      
      // Solid bright background for maximum contrast
      const buyBadgeBg = add.rectangle(badgeX, badgeY, badgeWidth, badgeHeight, 0xF3F300); // Bright yellow solid background
      buyBadgeBg.setAlpha(1.0);
      buyBadgeBg.setStrokeStyle(1, 0x374151);
      card.add(buyBadgeBg);
      
      // Badge text - black text on yellow background for maximum readability
      const buyText = add.text(badgeX, badgeY, `buy ${slotNumber}`, {
        fontSize: '18px', // Increased from 16px
        color: '#000000', // Black text for maximum contrast
        fontFamily: 'Arial, sans-serif',
        fontStyle: 'bold'
      }).setOrigin(0.5, 0.5);
      card.add(buyText);

      // Icon - 50% bigger (64 * 1.5 = 96), centered vertically in card
      const iconSize = 96;
      const iconBg = add.rectangle(-slotWidth / 2 + innerPadding + iconSize / 2, 0, iconSize, iconSize, 0x1F2937);
      iconBg.setStrokeStyle(1, 0x4B5563);
      // Add subtle inner glow for depth
      const iconBgInner = add.rectangle(iconBg.x, iconBg.y, iconSize - 2, iconSize - 2, 0x0F172A);
      iconBgInner.setAlpha(0.4);
      card.add(iconBgInner);
      card.add(iconBg);

      const iconImage = add.image(iconBg.x, iconBg.y, 'coin');
      iconImage.setVisible(false);
      iconImage.setScale(0.6);
      card.add(iconImage);

      const textStartX = iconBg.x + iconSize / 2 + 18;
      const textWidth = slotWidth / 2 - textStartX + slotWidth / 2 - innerPadding - 10; // Account for badge

      // Item name - positioned to align with centered icon
      const nameText = add.text(textStartX, -slotHeight / 2 + 40, 'Empty Slot', {
        fontSize: '22px',
        color: '#F3F4F6',
        fontFamily: 'Arial, sans-serif',
        fontStyle: 'bold',
        wordWrap: { width: Math.max(120, textWidth) }
      }).setOrigin(0, 0.5);
      card.add(nameText);

      // Price - moved up and larger font for better visibility
      const priceText = add.text(textStartX, nameText.y + 12, 'Awaiting item', {
        fontSize: '20px', // Increased from 18px
        color: '#FBBF24', // Gold/amber color for price
        fontFamily: 'Arial, sans-serif',
        fontStyle: 'bold',
        wordWrap: { width: Math.max(120, textWidth) }
      }).setOrigin(0, 0);
      card.add(priceText);

      // Effect - formatted better, smaller, adjusted spacing
      const effectText = add.text(textStartX, priceText.y + 24, '', {
        fontSize: '14px',
        color: '#9CA3AF',
        fontFamily: 'Arial, sans-serif',
        wordWrap: { width: Math.max(120, textWidth) }
      }).setOrigin(0, 0);
      card.add(effectText);

      this.shopSlots.push({
        container: card,
        background: cardBg,
        cardBgInner, // Store for potential updates
        buyText,
        buyBadgeBg, // Store badge background
        iconBg,
        iconBgInner, // Store for potential updates
        iconImage,
        nameText,
        priceText,
        effectText,
        slotNumber,
        itemData: null
      });
    }

    this.applyPendingShopItems();
  }

  setShopItems(items = []) {
    this.shopItemLookup.clear();
    this.pendingShopItems = Array.isArray(items) ? items : [];
    if (Array.isArray(this.pendingShopItems)) {
      this.pendingShopItems.forEach(item => this.registerShopItem(item));
    }
    this.itemsData = [...this.pendingShopItems];
    
    // Rebuild shop section if dimensions are available (to update slot count)
    if (this.sectionWidth && this.sectionHeight && this.shopContainer && this.scene) {
      this.buildShopSection(this.sectionWidth, this.sectionHeight);
    } else {
      this.applyPendingShopItems();
    }
  }

  async handleShopBuyCommand({ userId, wallet, username, slotNumber, quantity, player, rawArgs }) {
    const scene = this.scene;
    if (!scene) return false;

    console.log('[JoinShopManager] handleBuyCommand:', { userId, wallet, username, slotNumber, quantity, player, rawArgs });

    const targetPlayer = player || (userId != null && typeof scene.findPlayerByUserId === 'function'
      ? scene.findPlayerByUserId(userId)
      : null);

    const displayName = targetPlayer?.name || username || 'Player';

    if (!targetPlayer || !targetPlayer.hero) {
      this.showMessage(`❌ ${displayName} must join before buying items`, '#F87171');
      return false;
    }

    if (!targetPlayer.userId) {
      this.showMessage(`❌ Unable to identify player account for ${displayName}`, '#F87171');
      return false;
    }

    const heroId = targetPlayer.hero?.id;
    if (!heroId) {
      this.showMessage('❌ Missing hero information for this player', '#F87171');
      return false;
    }

    if (!Number.isFinite(slotNumber)) {
      this.showMessage('❌ Usage: buy <slot> [quantity]', '#F87171');
      return false;
    }

    const normalizedSlot = Math.max(1, Math.floor(Number(slotNumber)));
    const maxSlot = this.shopSlots.length;
    if (!maxSlot || normalizedSlot > maxSlot) {
      this.showMessage(`❌ Shop slot ${normalizedSlot} does not exist`, '#F87171');
      return false;
    }

    const slot = this.getShopSlotByNumber(normalizedSlot);
    const item = slot?.itemData;
    console.log('[JoinShopManager] item:', item);
    if (!item || !item.id) {
      this.showMessage(`❌ No item in shop slot ${normalizedSlot}`, '#F87171');
      return false;
    }

    if (typeof item.id !== 'number') {
      this.showMessage('❌ Shop is not available yet! Still in development...', '#F87171');
      //this.showMessage('❌ Shop item is unavailable. Please wait for the shop to load and try again.', '#F87171');
      return false;
    }

    let qty = Number.isFinite(quantity) ? Math.floor(quantity) : 1;
    if (!qty || qty < 1) qty = 1;
    qty = Math.min(qty, 9);

    const battleId = BattleStateManager.getBattleId();
    if (!battleId) {
      this.showMessage('❌ Shop is unavailable before a battle is created', '#F87171');
      return false;
    }

    const themeId = scene.currentThemeId || 'soulwars_default';
    const tokenSymbol = (item.tokenSymbol || this.tokenMeta?.symbol || 'soulwars').toLowerCase();
    const apiKey = process.env.REACT_APP_SOULWARS_API_KEY || 'soulwarz';

    try {
      const response = await fetch('/api/shop/buy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': apiKey
        },
        body: JSON.stringify({
          userId: targetPlayer.userId,
          heroId,
          battleId,
          shopState: 'join',
          shopType: 'join',
          shopItemId: item.id,
          quantity: qty,
          tokenSymbol,
          themeId,
        })
      });

      const payload = await response.json();
      if (!response.ok || !payload?.success) {
        const errorMsg = payload?.error || `Purchase failed (status ${response.status})`;
        this.showMessage(`❌ ${errorMsg}`, '#F87171');
        return false;
      }

      const result = payload.data;
      const normalizedInventory = Array.isArray(result?.inventory)
        ? result.inventory.map((entry) => ({ itemId: entry.item_id, qty: entry.quantity }))
        : [];

      const hero = targetPlayer.hero;
      hero.inventory = normalizedInventory;
      targetPlayer.inventory = normalizedInventory;

      if (targetPlayer.userId != null && typeof scene.updateAutoJoinRecord === 'function') {
        scene.updateAutoJoinRecord(targetPlayer.userId, {
          lastJoinSource: 'buy',
          lastJoinAt: Date.now(),
          lastJoinCommand: rawArgs
        });
        try {
          BattleStateManager.setPlayerInventory(targetPlayer.userId, hero.inventory);
        } catch (_) {}
      }

      this.refreshInventoryDisplay(targetPlayer);

      const chargedTokens = result?.tokenAmountCharged || item.tokenAmount || 0;
      const balanceLeft = result?.balance ?? null;
      const displayTokenSymbol = (item.tokenSymbol || tokenSymbol || 'soulwars').toUpperCase();
      const successMessageParts = [`🛒 ${displayName} bought ${qty}x ${item.name}`];
      if (chargedTokens) {
        successMessageParts.push(`-${chargedTokens.toLocaleString()} ${displayTokenSymbol}`);
      }
      if (balanceLeft != null) {
        successMessageParts.push(`Balance: ${balanceLeft.toLocaleString()} ${displayTokenSymbol}`);
      }
      this.showMessage(successMessageParts.join(' • '), '#10B981');

      // Emit purchase event for battlefield revenue sharing display
      if (scene && typeof scene.onShopPurchase === 'function') {
        const revenueShare = chargedTokens * 0.1; // 10% revenue share
        scene.onShopPurchase({
          tokenAmount: chargedTokens,
          revenueShare: revenueShare,
          tokenSymbol: displayTokenSymbol
        });
      }

      if (scene.showEmoteForWallet && wallet) {
        try { scene.showEmoteForWallet(wallet, '🛒'); } catch (_) {}
      }

      return true;
    } catch (error) {
      console.error('[JoinShopManager] buy failed:', error);
      this.showMessage('❌ Failed to complete purchase. Please try again.', '#F87171');
      return false;
    }
  }

  refreshInventoryDisplay(player, recordOverride) {
    if (!player) return;
    const record = recordOverride || this.findRecordForPlayer(player);
    if (!record) return;

    this.clearInventoryIcons(record);

    const inventory = Array.isArray(player?.hero?.inventory) ? player.hero.inventory : [];
    if (!inventory.length) {
      return;
    }

    const maxIcons = 9;
    const itemsToShow = inventory.slice(0, maxIcons);
    itemsToShow.forEach((entry, idx) => {
      if (!entry) return;
      const qty = Math.max(1, Number(entry.qty) || 1);
      const itemDef = this.getShopItemDefinition(entry.itemId) || { iconKey: null, name: entry.itemId };
      this.createInventoryIcon(record, itemDef, qty, idx);
    });
  }

  repositionInventoryIcons(record, cellWidth, cellHeight) {
    if (!record || !record.inventoryIcons) return;
    record.inventoryIcons.forEach(iconEntry => this.positionInventoryIcon(record, iconEntry, cellWidth, cellHeight));
  }

  applyPendingShopItems() {
    if (!Array.isArray(this.shopSlots) || !this.shopSlots.length) {
      return;
    }

    const items = Array.isArray(this.pendingShopItems) ? this.pendingShopItems : [];
    this.shopSlots.forEach((slot, index) => {
      const item = items[index];
      this.updateShopSlot(slot, item, index);
    });
  }

  registerPlayerRecord(record) {
    if (record && !record.inventoryIcons) {
      record.inventoryIcons = [];
    }
    if (record?.player) {
      this.refreshInventoryDisplay(record.player, record);
    }
  }

  registerShopItem(item) {
    if (!item) return;
    if (item.itemId) {
      this.shopItemLookup.set(item.itemId, item);
    }
    if (item.id != null) {
      this.shopItemLookup.set(String(item.id), item);
    }
  }

  getShopItemDefinition(itemId) {
    if (!itemId) return null;
    return this.shopItemLookup.get(String(itemId)) || null;
  }

  getShopSlotByNumber(slotNumber) {
    return this.shopSlots.find(slot => slot && slot.slotNumber === slotNumber) || null;
  }

  updateShopSlot(slot, item, index) {
    if (!slot) return;
    const defaultNumber = index + 1;
    const numberLabel = item?.number != null ? item.number : defaultNumber;
    slot.buyText.setText(`buy ${numberLabel}`);

    if (item && item.id != null) {
      this.registerShopItem(item);
      slot.itemData = item;
    } else {
      slot.itemData = null;
    }

    const name = item?.name || 'Empty Slot';
    slot.nameText.setText(name);
    slot.nameText.setFontSize(22);

    if (item?.iconKey && this.scene?.textures?.exists && this.scene.textures.exists(item.iconKey)) {
      slot.iconImage.setTexture(item.iconKey);
      slot.iconImage.setVisible(true);
      slot.iconImage.setScale(0.6);
      slot.iconImage.setRotation(0);
    } else {
      slot.iconImage.setVisible(false);
      slot.iconImage.setRotation(0);
    }

    // Price - separate and prominent, larger font
    if (item) {
      const priceAmount = (item.tokenAmount || 0).toLocaleString();
      const priceSymbol = item.tokenSymbol || 'SOULWARS';
      slot.priceText.setText(`${priceAmount} ${priceSymbol}`);
      slot.priceText.setColor('#FBBF24'); // Gold/amber
      slot.priceText.setFontSize(20); // Increased from 18px
      slot.priceText.setFontStyle('bold');
    } else {
      slot.priceText.setText('Awaiting item');
      slot.priceText.setColor('#9CA3AF');
      slot.priceText.setFontSize(16);
      slot.priceText.setFontStyle('normal');
    }

    // Effect - formatted better with more cases handled
    if (item?.effect) {
      const effectParts = [];
      let hasDuration = false;
      let dotInfo = null;
      
      Object.entries(item.effect).forEach(([key, value]) => {
        // Format common effects more readably
        if (key === 'heal') {
          effectParts.push(`Heals ${value} HP`);
        } else if (key === 'mana') {
          effectParts.push(`Restores ${value} Mana`);
        } else if (key === 'attackBoost') {
          effectParts.push(`+${(value * 100).toFixed(0)}% Attack`);
        } else if (key === 'defenseBoost') {
          effectParts.push(`+${(value * 100).toFixed(0)}% Defense`);
        } else if (key === 'speedBoost') {
          effectParts.push(`+${(value * 100).toFixed(0)}% Speed`);
        } else if (key === 'durationSec') {
          hasDuration = true;
          // Will add duration at the end
        } else if (key === 'dot') {
          // Store dot info to combine with ticks
          dotInfo = { damage: value, ticks: item.effect.ticks || 8 };
        } else if (key === 'ticks') {
          // Handled with dot
        } else {
          // Fallback for unknown effects - format nicely
          const formattedKey = key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1');
          effectParts.push(`${formattedKey}: ${value}`);
        }
      });
      
      // Handle DOT with ticks if present
      if (dotInfo) {
        effectParts.push(`${dotInfo.damage} damage/tick (${dotInfo.ticks} ticks)`);
      }
      
      // Add duration if present
      if (hasDuration && item.effect.durationSec) {
        effectParts.push(`(${item.effect.durationSec}s)`);
      }
      
      slot.effectText.setText(effectParts.join(' • '));
      slot.effectText.setColor('#9CA3AF');
      slot.effectText.setFontSize(14);
    } else {
      slot.effectText.setText('');
    }
  }

  clearInventoryIcons(record) {
    if (!record) return;
    if (Array.isArray(record.inventoryIcons)) {
      record.inventoryIcons.forEach(iconEntry => {
        if (!iconEntry) return;
        const { bg, icon, qtyText } = iconEntry;
        [bg, icon, qtyText].forEach(display => {
          try {
            if (display && typeof display.destroy === 'function') {
              display.destroy();
            }
          } catch (_) {}
        });
      });
    }
    record.inventoryIcons = [];
  }

  createInventoryIcon(record, itemDef, quantity, index) {
    if (!record || !record.container || !this.scene) return;

    const columns = 3;
    const row = Math.floor(index / columns);
    const col = index % columns;
    const { cellWidth, cellHeight } = this.gridConfig || {};
    const position = this.getInventoryIconPosition(row, col, cellWidth, cellHeight);

    const iconBg = this.scene.add.rectangle(position.x, position.y, 34, 34, 0x111827, 0.85);
    iconBg.setStrokeStyle(1, 0x374151);
    record.container.add(iconBg);

    let textureKey = itemDef?.iconKey || itemDef?.spriteKey || 'loot-chest';
    if (!this.scene.textures || !this.scene.textures.exists(textureKey)) {
      textureKey = 'loot-chest';
    }

    const iconImage = this.scene.add.image(position.x, position.y, textureKey);
    iconImage.setScale(0.32);
    record.container.add(iconImage);

    let qtyText = null;
    if (quantity > 1) {
      const qtyDisplay = quantity > 99 ? '99+' : `x${quantity}`;
      qtyText = this.scene.add.text(position.x + 12, position.y + 12, qtyDisplay, {
        fontSize: '12px',
        color: '#F9FAFB',
        fontFamily: 'Arial, sans-serif',
        stroke: '#111827',
        strokeThickness: 2
      }).setOrigin(1, 1);
      record.container.add(qtyText);
    }

    record.inventoryIcons = record.inventoryIcons || [];
    const iconEntry = { bg: iconBg, icon: iconImage, qtyText, row, col };
    record.inventoryIcons.push(iconEntry);
    this.positionInventoryIcon(record, iconEntry, cellWidth, cellHeight);
  }

  positionInventoryIcon(record, iconEntry, cellWidth, cellHeight) {
    if (!record || !iconEntry) return;
    const pos = this.getInventoryIconPosition(iconEntry.row, iconEntry.col, cellWidth, cellHeight);
    if (iconEntry.bg) iconEntry.bg.setPosition(pos.x, pos.y);
    if (iconEntry.icon) iconEntry.icon.setPosition(pos.x, pos.y);
    if (iconEntry.qtyText) iconEntry.qtyText.setPosition(pos.x + 12, pos.y + 12);
  }

  getInventoryIconPosition(row, col, cellWidth, cellHeight) {
    const fallbackCellWidth = cellWidth || this.gridConfig?.cellWidth || 200;
    const fallbackCellHeight = cellHeight || this.gridConfig?.cellHeight || 220;
    const startX = -fallbackCellWidth / 2 + 24;
    const startY = -fallbackCellHeight / 2 + 60;
    const stepX = 32;
    const stepY = 30;
    return {
      x: startX + col * stepX,
      y: startY + row * stepY
    };
  }

  findRecordForPlayer(player) {
    if (!player || !Array.isArray(this.scene?.playerDisplayObjects)) return null;
    return this.scene.playerDisplayObjects.find(obj => obj && obj.player === player) || null;
  }

  showMessage(text, color = '#F3F4F6') {
    try {
      showMarqueeMessage(text, { color }, 'medium');
    } catch (_) {}
  }
}

